import { isMobileViewport, isTouchDevice } from '@globals/utils/functions';
import TrapFocus from '@globals/utils/TrapFocus';
import { InteractionMode, InteractionStrategy } from './strategies/InteractionStrategy';
import HoverStrategy from './strategies/HoverStrategy';
import ClickStrategy from './strategies/ClickStrategy';

// Configuration: Change this to switch between 'click' or 'hover' interaction mode
const INTERACTION_MODE: InteractionMode = 'hover';

class MainHeader {
	header: HTMLDivElement | null;
	headerNav: HTMLDivElement | null;
	megaMenuItems: NodeListOf<Element> | null;
	megaMenuBackBtns: NodeListOf<HTMLButtonElement>;
	mobileMenuTrigger: HTMLButtonElement | null;
	adminBar: HTMLElement | null;
	isMobile: boolean;
	isTouch: boolean;
	breakpoints: IBreakpoints | undefined;
	focusableSelectors: string[];
	trapFocusInstance: TrapFocus | null = null;
	private strategy: InteractionStrategy | null = null;
	desktopSearchTrigger: HTMLAnchorElement | null;
	searchDropdown: HTMLDivElement | null;
	searchDropdownClose: HTMLButtonElement | null;
	searchDropdownInput: HTMLInputElement | null;
	private boundMegaMenuClickHandler: ((event: MouseEvent) => void) | null = null;

	constructor() {
		this.header = document.querySelector<HTMLDivElement>('.main-header');
		this.headerNav = this.header?.querySelector<HTMLDivElement>('.main-header__nav') || null;
		this.adminBar = document.getElementById('wpadminbar');
		this.megaMenuItems =
			this.headerNav?.querySelectorAll(
				'.header-nav-menu__nav-item--has-mm-dropdown, .header-nav-menu__nav-item--has-dropdown'
			) || null;
		this.megaMenuBackBtns = document.querySelectorAll<HTMLButtonElement>(
			'button[data-mega-menu-back]'
		);
		this.mobileMenuTrigger = document.querySelector<HTMLButtonElement>('.mobile-menu-trigger');
		this.desktopSearchTrigger = document.querySelector<HTMLAnchorElement>(
			'.main-header__desktop-search'
		);
		this.searchDropdown = document.querySelector<HTMLDivElement>(
			'.main-header__search-dropdown'
		);
		this.searchDropdownClose =
			document.querySelector<HTMLButtonElement>('.search-dropdown__close');
		this.searchDropdownInput =
			this.searchDropdown?.querySelector<HTMLInputElement>('input[name="s"]') || null;
		this.breakpoints = window.impBreakpoints;
		this.isMobile = false;
		this.isTouch = false;
		this.focusableSelectors = [
			'a[href]:not([tabindex="-1"])',
			'button:not([disabled]):not([tabindex="-1"])',
			'input:not([disabled]):not([tabindex="-1"])',
			'select:not([disabled]):not([tabindex="-1"])',
			'textarea:not([disabled]):not([tabindex="-1"])',
			'[tabindex]:not([tabindex="-1"])',
		];
		this.boundMegaMenuClickHandler = this.handleMegaMenuClick.bind(this);
	}

	init() {
		this.setHeaderHeight();

		if (this.breakpoints) {
			this.isMobile = isMobileViewport(this.breakpoints);
		}
		this.isTouch = isTouchDevice();

		this.initMenuStrategy();
		this.initMegaMenuClickListeners();

		if (this.mobileMenuTrigger) {
			this.mobileMenuTrigger.addEventListener('click', this.handleMobileMenuClick.bind(this));
		}

		this.megaMenuBackBtns.forEach((btn) => {
			btn.addEventListener('click', this.handleBackBtnClick.bind(this));
		});

		if (this.desktopSearchTrigger) {
			this.desktopSearchTrigger.addEventListener(
				'click',
				this.handleDesktopSearchClick.bind(this)
			);
		}

		if (this.searchDropdownClose) {
			this.searchDropdownClose.addEventListener(
				'click',
				this.handleSearchDropdownClose.bind(this)
			);
		}

		if (this.searchDropdownInput) {
			this.searchDropdownInput.addEventListener(
				'focus',
				this.handleSearchInputFocus.bind(this)
			);
			this.searchDropdownInput.addEventListener(
				'blur',
				this.handleSearchInputBlur.bind(this)
			);
		}
	}

	private initMenuStrategy(): void {
		if (!this.megaMenuItems) return;

		const callbacks = {
			onOpen: this.openMegaMenu.bind(this),
			onClose: this.closeMegaMenu.bind(this),
		};

		if (INTERACTION_MODE === 'hover') {
			this.strategy = new HoverStrategy(this.breakpoints);
		} else {
			this.strategy = new ClickStrategy(this.breakpoints);
		}

		this.strategy.init(this.megaMenuItems, callbacks);
	}

	private initMegaMenuClickListeners(): void {
		if (!this.megaMenuItems || !this.boundMegaMenuClickHandler) return;

		// Add click listeners ONLY for mobile and touch devices
		// In hover mode on desktop, clicking should navigate to the link
		if (this.isMobile || this.isTouch) {
			this.megaMenuItems.forEach((item) => {
				const link = item.querySelector('a');
				if (link && this.boundMegaMenuClickHandler) {
					// Remove existing listener to avoid duplicates
					link.removeEventListener('click', this.boundMegaMenuClickHandler);
					link.addEventListener('click', this.boundMegaMenuClickHandler);
				}
			});
		}
	}

	private removeMegaMenuClickListeners(): void {
		if (!this.megaMenuItems || !this.boundMegaMenuClickHandler) return;

		this.megaMenuItems.forEach((item) => {
			const link = item.querySelector('a');
			if (link && this.boundMegaMenuClickHandler) {
				link.removeEventListener('click', this.boundMegaMenuClickHandler);
			}
		});
	}

	resized() {
		this.setHeaderHeight();
		const wasMobile = this.isMobile;
		if (this.breakpoints) {
			this.isMobile = isMobileViewport(this.breakpoints);
		}
		this.isTouch = isTouchDevice();

		if (wasMobile !== this.isMobile && this.strategy) {
			this.strategy.destroy();
			this.initMenuStrategy();

			// Switching from desktop to mobile
			if (this.isMobile && !wasMobile) {
				this.initMegaMenuClickListeners();
				if (document.body.classList.contains('mobile-menu-open')) {
					this.closeMobileMenu();
				}
			}

			// Switching from mobile to desktop
			if (!this.isMobile && wasMobile) {
				this.removeMegaMenuClickListeners();
				this.resetMobileMenuForDesktop();
			}
		}

		if (this.isMobile && !wasMobile && this.searchDropdown?.classList.contains('active')) {
			this.handleSearchDropdownClose();
		}
	}

	loaded() {
		this.setHeaderHeight();
		if (this.breakpoints) {
			this.isMobile = isMobileViewport(this.breakpoints);
		}
		this.isTouch = isTouchDevice();
	}

	handleMobileMenuClick() {
		if (this.isMobile && this.headerNav) {
			if (!document.body.classList.contains('mobile-menu-open')) {
				document.body.classList.add('mobile-menu-open');
				document.body.classList.remove('mobile-mm-open');
				this.handleBackBtnClick();
				this.trapMobileMenu();
			} else {
				this.closeMobileMenu();
			}
		}
	}

	closeMobileMenu() {
		if (!this.headerNav) return;

		document.body.classList.remove('mobile-menu-open');
		document.body.classList.remove('mobile-mm-open');

		this.megaMenuBackBtns.forEach((btn) => {
			btn.classList.remove('is-open');
		});

		const activeDropdowns = this.headerNav.querySelectorAll<HTMLElement>(
			'.header-nav-menu__nav-item.active'
		);
		activeDropdowns.forEach((item) => {
			item.classList.remove('active');
			const dropdown = item.querySelector<HTMLElement>(
				'.header-nav-menu__dropdown, .mm-dropdown'
			);
			if (dropdown) {
				dropdown.classList.remove('active');
			}
		});

		this.megaMenuItems?.forEach((item) => {
			const link = item.querySelector('a');
			if (link) {
				const prevTabindex = link.getAttribute('data-prev-tabindex');
				if (prevTabindex) {
					link.setAttribute('tabindex', prevTabindex);
				} else {
					link.removeAttribute('tabindex');
				}
			}
		});
	}

	resetMobileMenuForDesktop() {
		if (!this.headerNav) return;

		const activeItems = this.headerNav.querySelectorAll<HTMLElement>(
			'.header-nav-menu__nav-item.active'
		);
		activeItems.forEach((item) => {
			item.classList.remove('active');
		});

		const allDropdowns = this.headerNav.querySelectorAll<HTMLElement>(
			'.header-nav-menu__dropdown, .mm-dropdown'
		);
		allDropdowns.forEach((dropdown) => {
			dropdown.classList.remove('active', 'is-closing');
		});

		document.body.classList.remove('mobile-menu-open', 'mobile-mm-open', 'mm-closing');

		this.megaMenuBackBtns.forEach((btn) => {
			btn.classList.remove('is-open');
		});

		this.megaMenuItems?.forEach((item) => {
			const link = item.querySelector('a');
			if (link) {
				const prevTabindex = link.getAttribute('data-prev-tabindex');
				if (prevTabindex) {
					link.setAttribute('tabindex', prevTabindex);
				} else {
					link.removeAttribute('tabindex');
				}
			}
		});
	}

	trapMobileMenu() {
		if (!this.headerNav || !this.mobileMenuTrigger || !this.header) return;

		const focusableElements = Array.from(
			this.headerNav.querySelectorAll(this.focusableSelectors.join(','))
		) as HTMLElement[];
		focusableElements.unshift(this.mobileMenuTrigger);

		this.removeTrapFocus();

		this.trapFocusInstance = new TrapFocus(
			this.header,
			focusableElements,
			() => this.handleMobileMenuClick(),
			this.mobileMenuTrigger
		);
	}

	trapMegaMenu(megaMenuContainer: HTMLElement) {
		if (!this.header || !this.mobileMenuTrigger) return;

		const focusableElements = Array.from(
			megaMenuContainer.querySelectorAll(this.focusableSelectors.join(','))
		) as HTMLElement[];

		focusableElements.unshift(this.mobileMenuTrigger);

		this.megaMenuItems?.forEach((item) => {
			const link = item.querySelector('a');
			if (link) {
				const prevTabindex = link.getAttribute('data-prev-tabindex');
				if (prevTabindex) {
					link.setAttribute('data-prev-tabindex', prevTabindex);
				} else {
					link.setAttribute('tabindex', '-1');
				}
			}
		});

		this.removeTrapFocus();

		this.trapFocusInstance = new TrapFocus(
			this.header,
			focusableElements,
			() => {
				// Close the entire mobile menu on escape, not just the mega menu
				this.handleMobileMenuClick();
			},
			this.mobileMenuTrigger
		);
	}

	removeTrapFocus() {
		if (this.trapFocusInstance) {
			this.trapFocusInstance.deactivate();
			this.trapFocusInstance = null;
		}
	}

	handleBackBtnClick() {
		const activeMegaMenu = document.querySelector<HTMLDivElement>(
			'.header-nav-menu__nav-item.active'
		);

		if (null !== activeMegaMenu) {
			activeMegaMenu.classList.remove('active');
			this.closeMobileMegaMenu(activeMegaMenu);
		}
	}

	openMegaMenu(target: HTMLElement) {
		const megaMenu = target.querySelector<HTMLDivElement>(
			'.mm-dropdown, .header-nav-menu__dropdown'
		);
		const navLink = target.querySelector<HTMLAnchorElement>('.header-nav-menu__nav-link');
		if (megaMenu && navLink) {
			navLink.classList.add('is-open');
			megaMenu.classList.add('is-open');
		}
	}

	closeMegaMenu(target: HTMLElement) {
		const megaMenu = target.querySelector<HTMLDivElement>(
			'.mm-dropdown, .header-nav-menu__dropdown'
		);
		const navLink = target.querySelector<HTMLAnchorElement>('.header-nav-menu__nav-link');
		if (megaMenu && navLink) {
			navLink.classList.remove('is-open');
			megaMenu.classList.remove('is-open');
		}
	}

	handleMegaMenuClick(event: MouseEvent) {
		const target = event.currentTarget as HTMLAnchorElement;
		const parent = target.parentElement as HTMLDivElement;
		if (!parent) return;

		if (this.isMobile) {
			// Mobile behavior - prevent default for dropdown items
			event.preventDefault();
			if (parent.classList.contains('active')) {
				parent.classList.remove('active');
				this.closeMobileMegaMenu(target, true);
			} else {
				parent.classList.add('active');
				this.openMobileMegaMenu(target, true);

				const dropdown = parent.querySelector<HTMLElement>(
					'.mm-dropdown, .header-nav-menu__dropdown'
				);

				if (dropdown) {
					this.trapMegaMenu(dropdown);
				}
			}
		} else if (this.isTouch) {
			if (target.classList.contains('is-open')) {
				event.preventDefault();
				this.closeMegaMenu(parent);
			} else {
				const openMenuItems = document.querySelectorAll<HTMLAnchorElement>(
					'.header-nav-menu__nav-link.is-open'
				);

				openMenuItems.forEach((openMenuItem) => {
					if (openMenuItem.parentElement && openMenuItem.parentElement !== parent) {
						this.closeMegaMenu(openMenuItem.parentElement);
					}
				});

				event.preventDefault();
				this.openMegaMenu(parent);
			}
		}
	}

	openMobileMegaMenu(item: HTMLAnchorElement | HTMLDivElement, isLink = false) {
		const li = isLink ? item.parentElement : item;
		if (!li) return;
		const megaMenu = li.querySelector<HTMLDivElement>(
			'.mm-dropdown, .header-nav-menu__dropdown'
		);
		if (megaMenu) {
			megaMenu.classList.add('active');
			document.body.classList.add('mobile-mm-open');
			this.megaMenuBackBtns.forEach((btn) => {
				btn.classList.add('is-open');
			});
		}
	}

	closeMobileMegaMenu(item: HTMLAnchorElement | HTMLDivElement, isLink = false) {
		const li = isLink ? item.parentElement : item;
		if (!li) return;
		const megaMenu = li.querySelector<HTMLDivElement>(
			'.mm-dropdown, .header-nav-menu__dropdown'
		);
		if (megaMenu) {
			document.body.classList.add('mm-closing');
			megaMenu.classList.add('is-closing');
			megaMenu.classList.remove('active');
			this.megaMenuBackBtns.forEach((btn) => {
				btn.classList.remove('is-open');
			});
			document.body.classList.remove('mobile-mm-open');

			setTimeout(() => {
				document.body.classList.remove('mm-closing');
				megaMenu.classList.remove('is-closing');
			}, 300);
		}
	}

	setHeaderHeight() {
		if (!this.header) return;

		const headerHeight = this.header.offsetHeight;
		let headerHeightFull = this.header.offsetHeight;

		if (null !== this.adminBar) {
			headerHeightFull += this.adminBar.offsetHeight;
		}

		document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
		document.documentElement.style.setProperty('--headerHeight', headerHeight + 'px');
		document.documentElement.style.setProperty('--headerHeightFull', headerHeightFull + 'px');
	}

	handleDesktopSearchClick(event: MouseEvent) {
		event.preventDefault();
		if (this.searchDropdown) {
			if (this.searchDropdown.classList.contains('active')) {
				this.searchDropdown.classList.remove('active');
			} else {
				this.searchDropdown.classList.add('active');
				// Only focus input when opening
				if (this.searchDropdownInput) {
					this.searchDropdownInput.focus();
				}
			}
		}
	}

	handleSearchDropdownClose() {
		if (this.searchDropdown) {
			this.searchDropdown.classList.remove('active');
		}
	}

	handleSearchInputFocus() {
		if (this.searchDropdownInput) {
			this.searchDropdownInput.placeholder = 'Start typing to search';
		}
	}

	handleSearchInputBlur() {
		if (this.searchDropdownInput) {
			this.searchDropdownInput.placeholder = 'What are you looking for?';
		}
	}
}

export default new MainHeader();
